import { supabase } from '../../supabase';
import { Game as LibGame, RecommendationFilters } from '../../../types';
import { Game as IndexGame } from '../../../types/index';
import { DatabaseUtils } from '../utils/databaseUtils';
import { GameProcessingUtils } from '../utils/gameProcessingUtils';
import { RECOMMENDATION_CONFIG } from '..';

/**
 * Serviço centralizado que gerencia dados comuns para todos os recomendadores
 * Implementa cache e otimizações para evitar chamadas redundantes ao banco de dados
 */
export class RecommendationDataService {
  // Cache para dados frequentemente acessados
  private static cache: {
    userPreferences: Map<string, { 
      data: {
        categories: Set<string>;
        mechanics: Set<string>;
        themes: Set<string>;
        gameIds: Set<string>;
        preferredComplexity?: number;
        minPlayers?: number;
        maxPlayers?: number;
        maxPlayTime?: number;
      }, 
      timestamp: number 
    }>;
    popularGames: { data: IndexGame[], timestamp: number };
    recentGames: { data: IndexGame[], timestamp: number };
    userFeedback: Map<string, { likes: Set<string>, dislikes: Set<string>, timestamp: number }>;
  } = {
    userPreferences: new Map(),
    popularGames: { data: [], timestamp: 0 },
    recentGames: { data: [], timestamp: 0 },
    userFeedback: new Map()
  };

  /**
   * Obtém preferências de um usuário (categorias, complexidade, etc)
   * com sistema de cache para evitar consultas repetidas
   */
  public static async getUserPreferences(userId: string): Promise<{
    categories: Set<string>;
    mechanics: Set<string>;
    themes: Set<string>;
    gameIds: Set<string>;
    preferredComplexity?: number;
    minPlayers?: number;
    maxPlayers?: number;
    maxPlayTime?: number;
  }> {
    // Verificar se temos em cache e se ainda é válido
    const cachedData = this.cache.userPreferences.get(userId);
    const now = Date.now();
    
    if (cachedData && (now - cachedData.timestamp) < RECOMMENDATION_CONFIG.cache.defaultExpirationMs) {
      return cachedData.data;
    }
    
    try {
      // Buscar jogos favoritos (rating >= 3) do usuário
      console.log(`[DataService DEBUG] getUserPreferences - Buscando jogos favoritos (rating >= 3) para ${userId}`);
      const favoriteRatedGames: IndexGame[] = await DatabaseUtils.getUserFavoriteGames(userId) as IndexGame[];
      console.log(`[DataService DEBUG] getUserPreferences - Encontrados ${favoriteRatedGames.length} jogos favoritos com rating >= 3`);
      
      // === INÍCIO DA ADIÇÃO: Buscar jogos explicitamente curtidos ===
      console.log(`[DataService DEBUG] getUserPreferences - Buscando feedback explícito (likes) para ${userId}`);
      const { likes: likedGameIdsSet } = await this.getUserFeedback(userId);
      const likedGameIds = Array.from(likedGameIdsSet);
      console.log(`[DataService DEBUG] getUserPreferences - Encontrados ${likedGameIds.length} IDs de jogos curtidos explicitamente`);
      let likedGames: IndexGame[] = [];

      if (likedGameIds.length > 0) {
        // Buscar dados completos dos jogos curtidos
        const { data: likedGamesData, error: likedGamesError } = await supabase
          .from('games')
          .select('*')
          .in('id', likedGameIds);

        if (likedGamesError) {
          console.error('Erro ao buscar dados dos jogos curtidos:', likedGamesError);
        } else {
          likedGames = (likedGamesData as IndexGame[]) || [];
        }
      }
      // === FIM DA ADIÇÃO ===

      // Combinar as listas de jogos favoritos e curtidos, removendo duplicatas
      console.log(`[DataService DEBUG] getUserPreferences - Combinando ${favoriteRatedGames.length} jogos favoritos e ${likedGames.length} jogos curtidos explicitamente`);
      const combinedGamesMap = new Map<string, IndexGame>();
      favoriteRatedGames.forEach(game => game && game.id && combinedGamesMap.set(game.id, game));
      likedGames.forEach(game => game && game.id && combinedGamesMap.set(game.id, game));
      const allPositiveInteractionGames: IndexGame[] = Array.from(combinedGamesMap.values());
      console.log(`[DataService DEBUG] getUserPreferences - Total de ${allPositiveInteractionGames.length} jogos únicos com interação positiva encontrados`);
      
      // Log do array ANTES de passar para a extração (usar o array original IndexGame[])
      // console.log('[DataService DEBUG] getUserPreferences - Conteúdo de allPositiveInteractionGames:', allPositiveInteractionGames);

      // Extrair atributos favoritos da lista original (IndexGame[])
      console.log('[DataService DEBUG] getUserPreferences - Extraindo atributos favoritos...');
      // Passar o array original IndexGame[] diretamente
      const userAttributes = GameProcessingUtils.extractFavoriteAttributes(allPositiveInteractionGames);
      console.log(`[DataService DEBUG] getUserPreferences - Atributos extraídos: ${userAttributes.categories.size} categorias, ${userAttributes.mechanics.size} mecânicas, ${userAttributes.themes.size} temas`);
      
      // Buscar informações de jogadores e tempo de jogo
      console.log('[DataService DEBUG] getUserPreferences - Buscando informações do perfil...');
      const { data: profile } = await supabase
        .from('profiles')
        .select('preferred_player_count, max_play_time')
        .eq('id', userId)
        .single();
        
      // Adicionar informações do perfil
      const result = {
        ...userAttributes,
        minPlayers: profile?.preferred_player_count,
        maxPlayers: profile?.preferred_player_count,
        maxPlayTime: profile?.max_play_time
      };
      
      // Armazenar em cache
      this.cache.userPreferences.set(userId, {
        data: result,
        timestamp: now
      });
      
      return result;
    } catch (error) {
      console.error('Erro ao obter preferências do usuário:', error);
      
      // Valor padrão em caso de erro
      const defaultPreferences = {
        categories: new Set(['Estratégia', 'Familiar', 'Party Game']),
        mechanics: new Set([]),
        themes: new Set([]),
        gameIds: new Set([])
      };
      
      return defaultPreferences;
    }
  }

  /**
   * Obtém feedback do usuário (likes/dislikes)
   */
  public static async getUserFeedback(userId: string): Promise<{
    likes: Set<string>;
    dislikes: Set<string>;
  }> {
    // Verificar cache
    const cachedData = this.cache.userFeedback.get(userId);
    const now = Date.now();
    
    if (cachedData && (now - cachedData.timestamp) < RECOMMENDATION_CONFIG.cache.defaultExpirationMs) {
      return {
        likes: cachedData.likes,
        dislikes: cachedData.dislikes
      };
    }
    
    try {
      // Buscar recomendações com feedback positivo
      const { data: likedRecs, error: likesError } = await supabase
        .from('recommendations')
        .select('game_id')
        .eq('user_id', userId)
        .eq('liked', true);
        
      // Buscar recomendações com feedback negativo
      const { data: dislikedRecs, error: dislikesError } = await supabase
        .from('recommendations')
        .select('game_id')
        .eq('user_id', userId)
        .eq('liked', false);
      
      if (likesError) console.error('Erro ao buscar likes:', likesError);
      if (dislikesError) console.error('Erro ao buscar dislikes:', dislikesError);
      
      // Extrair IDs e converter para Set
      const likes = new Set(likedRecs?.map(rec => rec.game_id) || []);
      const dislikes = new Set(dislikedRecs?.map(rec => rec.game_id) || []);
      
      // Armazenar em cache
      this.cache.userFeedback.set(userId, {
        likes,
        dislikes,
        timestamp: now
      });
      
      return { likes, dislikes };
    } catch (error) {
      console.error('Erro ao obter feedback do usuário:', error);
      return { likes: new Set(), dislikes: new Set() };
    }
  }

  /**
   * Aplica filtros automaticamente baseados nas preferências do usuário
   */
  public static async applySmartFilters(
    userId: string, 
    baseFilters?: RecommendationFilters
  ): Promise<RecommendationFilters> {
    try {
      // Obter preferências do usuário
      const preferences = await this.getUserPreferences(userId);
      const result: RecommendationFilters = { ...baseFilters };
      
      // Se não há filtros específicos, usar preferências do usuário
      if (!result.minPlayers && preferences.minPlayers) {
        result.minPlayers = preferences.minPlayers;
      }
      
      if (!result.maxPlayers && preferences.maxPlayers) {
        result.maxPlayers = preferences.maxPlayers;
      }
      
      if (!result.maxPlayTime && preferences.maxPlayTime) {
        result.maxPlayTime = preferences.maxPlayTime;
      }
      
      // Ajustar complexidade baseada na preferência do usuário
      if (!result.minComplexity && preferences.preferredComplexity) {
        result.minComplexity = Math.max(1, preferences.preferredComplexity - 1);
      }
      
      if (!result.maxComplexity && preferences.preferredComplexity) {
        result.maxComplexity = Math.min(5, preferences.preferredComplexity + 1);
      }
      
      return result;
    } catch (error) {
      console.error('Erro ao aplicar filtros inteligentes:', error);
      return baseFilters || {};
    }
  }

  /**
   * Obtém jogos populares (com cache)
   */
  public static async getPopularGames(
    count: number, 
    excludeIds: string[] = []
  ): Promise<IndexGame[]> {
    // Verificar cache de jogos populares
    const cachedPopular = this.cache.popularGames;
    const now = Date.now();
    
    if (cachedPopular.data.length > 0 && (now - cachedPopular.timestamp) < RECOMMENDATION_CONFIG.cache.defaultExpirationMs) {
      // Retornar dados do cache se válidos
      return GameProcessingUtils.filterExcludedGames(cachedPopular.data, excludeIds).slice(0, count);
    }
    
    try {
      console.log(`[DataService DEBUG] getPopularGames - Buscando ${count} jogos populares, excluindo ${excludeIds.length} IDs.`);
      // DatabaseUtils.getPopularGames agora usa RECOMMENDATION_CONFIG.popular.query.initialLimit internamente
      // e não aceita mais um parâmetro de limite.
      const games: IndexGame[] = await DatabaseUtils.getPopularGames(excludeIds);
      console.log(`[DataService DEBUG] getPopularGames - Encontrados ${games.length} jogos populares de DatabaseUtils.`);
      
      // Armazenar em cache (como IndexGame[])
      this.cache.popularGames = { data: games, timestamp: now };
      
      // Retornar a quantidade solicitada
      return games.slice(0, count);
    } catch (error) {
      console.error('Erro ao buscar jogos populares:', error);
      
      // Tentar usar o cache mesmo se expirado
      if (this.cache.popularGames.data.length > 0) {
        // Correção: Usar o método filterExcludedGames
        const cachedGames = GameProcessingUtils.filterExcludedGames(
          this.cache.popularGames.data,
          excludeIds
        );
        if (cachedGames.length > 0) {
          console.warn('Retornando jogos populares do cache expirado devido a erro.');
          return cachedGames.slice(0, count);
        }
      }
      
      return [];
    }
  }

  /**
   * Obtém jogos recentes (com cache)
   */
  public static async getRecentGames(
    count: number, 
    excludeIds: string[] = []
  ): Promise<IndexGame[]> {
    // Verificar cache de jogos recentes
    const cachedRecent = this.cache.recentGames;
    const now = Date.now();
    
    if (cachedRecent.data.length > 0 && (now - cachedRecent.timestamp) < RECOMMENDATION_CONFIG.cache.defaultExpirationMs) {
      // Retornar dados do cache se válidos
      return GameProcessingUtils.filterExcludedGames(cachedRecent.data, excludeIds).slice(0, count);
    }
    
    try {
      console.log(`[DataService DEBUG] getRecentGames - Buscando ${count} jogos recentes, excluindo ${excludeIds.length} IDs.`);
      // Assumindo que DatabaseUtils.getRecentGames retorna IndexGame[]
      // Correção: Inverter ordem dos argumentos
      const games: IndexGame[] = await DatabaseUtils.getRecentGames(excludeIds, count * 2);
      console.log(`[DataService DEBUG] getRecentGames - Encontrados ${games.length} jogos recentes.`);

      // Armazenar em cache (como IndexGame[])
      this.cache.recentGames = { data: games, timestamp: now };
      
      // Retornar a quantidade solicitada
      return games.slice(0, count);
    } catch (error) {
      console.error('Erro ao buscar jogos recentes:', error);
      
      // Tentar usar o cache mesmo se expirado
      if (this.cache.recentGames.data.length > 0) {
        const cachedGames = GameProcessingUtils.filterExcludedGames(
          this.cache.recentGames.data,
          excludeIds
        );
        if (cachedGames.length > 0) {
          console.warn('Retornando jogos recentes do cache expirado devido a erro.');
          return cachedGames.slice(0, count);
        }
      }
      return [];
    }
  }

  /**
   * Limpa o cache para um usuário específico ou todo o cache
   */
  public static clearCache(userId?: string): void {
    if (userId) {
      this.cache.userPreferences.delete(userId);
      this.cache.userFeedback.delete(userId);
      console.log(`Cache limpo para o usuário: ${userId}`);
    } else {
      this.cache.userPreferences.clear();
      this.cache.userFeedback.clear();
      this.cache.popularGames = { data: [], timestamp: 0 };
      this.cache.recentGames = { data: [], timestamp: 0 };
      console.log('Todo o cache foi limpo');
    }
  }

  /**
   * Obtém IDs de jogos a serem excluídos da recomendação
   */
  public static async getExcludeIds(
    userId: string, 
    includeOwned: boolean = true, 
    includeLiked: boolean = false
  ): Promise<string[]> {
    try {
      const resultIds: string[] = [];
      
      // 1. Obter IDs de jogos que o usuário não gostou (sempre excluir)
      const { dislikes, likes } = await this.getUserFeedback(userId);
      resultIds.push(...dislikes);
      
      // 2. Se solicitado, incluir jogos que o usuário já possui
      if (includeOwned) {
        const { data: userGames, error } = await supabase
          .from('user_games')
          .select('game_id')
          .eq('user_id', userId)
          .eq('owned', true);
          
        if (!error && userGames) {
          resultIds.push(...userGames.map(ug => ug.game_id));
        }
      }
      
      // 3. Se não deve incluir jogos curtidos, mas há algum curtido
      if (!includeLiked && likes.size > 0) {
        // Filtrar os IDs para remover os curtidos (se já estiverem na lista por outro motivo)
        return resultIds.filter(id => !likes.has(id));
      }
      
      return resultIds;
    } catch (error) {
      console.error('Erro ao obter IDs de exclusão:', error);
      return [];
    }
  }
} 